
import CatalogsFillsService from '@/services/CatalogsFillsService'
import CatalogsService from '@/services/CatalogsService'
import userInfoStore from '@/store/userInfo'
import { Catalog, DataCatalog } from '@/types/catalogs/Catalogs'
import { ProjectCatalogFilters } from '@/types/catalogs/Projects'
import { PageRequest } from '@/types/Support'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import DateFormat from '@/services/DateFormat'
import FileForTable, { FILE_EXTENSION } from '@/services/files/FileUtils'
import { STRING } from '@/types/GeneralConstants'

@Component({
  name: 'CatalogForm',
  components: {
    LoadingMask
  }
})
export default class CatalogForm extends Vue {
  @Prop({ required: false, default: -1 })
  catalogId: number

  @Prop({ required: true })
  title: string

  @Prop({ required: false, default: true })
  readOnly: boolean

  @Prop({ required: false, default: () => [] })
  domains: any[]

  @Prop({ required: false, default: () => [] })
  inheritedProjects: any[]

  @Prop({ required: false, default: false })
  canModify: boolean

  @Prop({ required: false, default: false })
  canDelete: boolean

  @Prop({ required: false, default: () => {} })
  handlesAPIErrors: any

  PAGE_SIZE_MAX = 500
  FILE_EXTENSION = FILE_EXTENSION
  catalogItem: Catalog = Catalog.getInstanceForEdit()
  backupCatalogItem: string = ''
  loadingData: boolean = false
  savingData: boolean = false
  loadingProjects: boolean = false
  readingFile: boolean = false
  dataTableFilter: string = ''
  dataTableHeaders: any[] = []
  dataTableContent: any[] = []
  totalRows: number = 0
  currentPage: number = 1
  perPage: number = 20
  data: DataCatalog = new DataCatalog()
  dataCopyPaste: string = ''
  fileImport: File | null = null
  adjusmentInRows: boolean = false

  projects: any[] = []

  creationDate: string = ''
  updateDate: string = ''

  /** Lifecycle hook for the component */
  mounted () {
    this.init()
  }

  /** Loads the form values */
  async init () {
    if (!this.existCatalog) {
      this.catalogItem.domain = userInfoStore.userInfo.domain.id
      this.sincronizeDataForTable()
      this.loadProjects()
    } else {
      this.loadCatalog()
    }
  }

  /**
   * @returns true if the current catalog has content
   */
  get isCatalogContent (): boolean {
    return this.existCatalog && this.isCatalogContentData
  }

  /** @returns true if the user belongs to root domain */
  get inRootDomain () {
    return userInfoStore.inRootDomain()
  }

  /**
   * @returns true if exist a positive id by a catalog
   */
  get existCatalog () {
    return this.catalogId > 0
  }

  /**
   * @returns true if exist a catalog and the context
   * is read only to try edit the catalog
   */
  get canModifyCatalog () {
    return this.canModify && this.existCatalog && this.readOnly
  }

  /**
   * @returns true if the catalog item has some columns and headers,
   * otherwise false
   */
  get isCatalogContentData () {
    let data = this.catalogItem.data
    return data && data.headers && data.content && data.headers.length > 0 && data.content.length > 0
  }

  get isEmptyCellsInContent () {
    return this.catalogItem.data.content.some(row => row.some(cell => cell === STRING.EMPTY_STRING))
  }

  /** Loads the catalog selected */
  async loadCatalog () {
    if (this.loadingData) return

    await this.getCatalog()
  }

  /** Gets the current projects when its has not been already loaded */
  loadProjects () {
    if (this.inheritedProjects.length && !this.inRootDomain) {
      this.projects = this.inheritedProjects
    } else {
      this.getProjects(this.pageRequestProjects())
    }
  }

  /**
   * Gets the current catalog by its id and assign it
   * in the 'catalogItem' variable
   */
  async getCatalog () {
    this.loadingData = true
    await CatalogsFillsService.getCatalog(this.catalogId)
      .then((response) => {
        this.catalogItem = response
        this.creationDate = response.creationDate ? new DateFormat(response.creationDate).toDateTimeString() : '- - / - - / - - - -'
        this.updateDate = response.updateDate ? new DateFormat(response.updateDate).toDateTimeString() : '- - / - - / - - - -'
        this.backupCatalogItem = JSON.stringify(response)
        this.loadProjects()
      })
      .finally(() => {
        this.loadingData = false
        this.sincronizeDataForTable()
      })
  }

  /**
   * Gets all the projects
   *
   * @param pageRequest with the configurations to the resquest
   */
  async getProjects (pageRequest: PageRequest<ProjectCatalogFilters>) {
    this.loadingProjects = true
    CatalogsService.getProjects(pageRequest)
      .then((response) => {
        this.projects.push(
          ...response.results
            .map((p) => {
              return { 'value': p.id, 'text': p.name }
            })
        )
      })
      .finally(() => {
        this.projects.unshift(
          { 'value': null, 'text': '-- Seleccionar proyecto --' }
        )
        this.loadingProjects = false
        this.updateInheritProjects(this.projects)
      })
  }

  /**
   * Prepares a page request to get te projects
   *
   * @returns a page request
   */
  pageRequestProjects () {
    let pageRequestProjects = new PageRequest<ProjectCatalogFilters>()
    if (this.inRootDomain) {
      pageRequestProjects.filters.domain = this.catalogItem.domain
    }
    return pageRequestProjects
  }

  /**
   * Updates the project list, this happends when the domain value changes
   */
  updateProjects () {
    this.projects = []
    this.getProjects(this.pageRequestProjects())
    this.catalogItem.project = null
  }

  /**
   * Emits the event to update the inheritedProjects
   *
   * @param projects current project list
   */
  updateInheritProjects (projects: any[]) {
    this.$emit('updateInheritProjects', projects)
  }

  /**
   * Saves the catalog values
   */
  async onSubmit () {
    this.$validator.validateAll().then(validations => {
      if (!validations) {
        return
      }
      let creatingCatalog = !this.catalogItem.id
      if (this.backupCatalogItem !== JSON.stringify(this.catalogItem)) {
        CatalogsFillsService.saveCatalog(this.catalogItem)
          .then((response) => {
            if (creatingCatalog) {
              this.$emit('addNewItem', response)
            } else {
              this.$emit('updatesItem', response)
            }
            this.onCloseForm()
          })
          .catch((error) => {
            this.handlesAPIErrors('Error al guardar el catálogo', error)
          })
      } else {
        this.onCloseForm()
      }
    })
  }

  /**
   * Emits the event to close the form view
   */
  onCloseForm () {
    this.$emit('closeForm')
  }

  /**
   * Emits the event to change the context to can edit the form
   */
  onEnableForm () {
    this.$emit('enableForm')
  }

  /**
   * Emits the event to delete a record
   */
  onDeleteForm () {
    this.$emit('deleteForm')
  }

  /**
   * Checks if any form field launchs an error
   * based on its validations
   *
   * @param ref to the field
   */
  validateState (ref: any) {
    const thiz = this as any
    if (
      thiz.veeFields[ref] &&
      (thiz.veeFields[ref].dirty || thiz.veeFields[ref].validated)
    ) {
      return !thiz.veeErrors.has(ref)
    }
    return null
  }

  /** Clears the values for the data in the catalog */
  clearDataCatalog () {
    this.catalogItem.data = new DataCatalog()
    this.dataCopyPaste = STRING.EMPTY_STRING
  }

  /** Clears the last file imported */
  clearFileImport () {
    this.fileImport = null
  }

  /**
   * Reads the XLSX, CSV, TSV file selected to import
   *
   * @param event - onchange event
   */
  async handleFile (event: any) {
    this.adjusmentInRows = false
    const file: File = event.target.files[0]
    if (!file) return

    const fileExtension = FileForTable.getFileExtension(file)
    if (!Object.values(FILE_EXTENSION).includes(fileExtension as FILE_EXTENSION)) {
      this.$bvToast.toast(
        `El archivo seleccionado no cuenta con una extensión válida.
         Por favor, intenta con un archivo que tenga alguna de las siguientes extensiones: ${Object.values(FILE_EXTENSION).map(f => `.${f}`).join(', ')}`,
        { title: 'Error con la extensión del archivo.', variant: 'danger', autoHideDelay: 6000 }
      )
      return
    }
    let results = { headers: [] as string[], rows: [] as string[][] }
    if (Object.values(FILE_EXTENSION).filter(f => f.startsWith('xl') || f === 'csv').includes(fileExtension as FILE_EXTENSION)) {
      results = await FileForTable.readExcel(file, this.clearDataCatalog)
    } else if (Object.values(FILE_EXTENSION).filter(f => f === FILE_EXTENSION.TSV || f === FILE_EXTENSION.TAB).includes(fileExtension as FILE_EXTENSION)) {
      results = await FileForTable.readTSV(file, this.clearDataCatalog)
    }
    const nHeaders = results.headers.length
    if (this.areMoreRowValuesThanHeaders(nHeaders, results.rows)) {
      results.rows = results.rows.map(row => row.slice(0, nHeaders))
      this.adjusmentInRows = true
    }
    this.catalogItem.data.headers = results.headers
    this.catalogItem.data.content = results.rows
    this.sincronizeDataForTable()
  }

  areMoreRowValuesThanHeaders (numberOfHeaders: number, rows: string[][]): boolean {
    return rows.some(row => row.length > numberOfHeaders)
  }

  @Watch('dataCopyPaste')
  handleDataCopyPaste () {
    if (this.dataCopyPaste) {
      this.clearFileImport()
      let dataRows = this.dataCopyPaste.split('\n')
      let headers: string[] = []
      let content: string[][] = []
      if (dataRows.length) {
        headers = dataRows[0].split('\t').filter(header => header !== STRING.EMPTY_STRING)
        content = dataRows.slice(1)
          .map((row) => row.split('\t'))
        content = content.map(row => {
          const headersLenght = headers.length
          // When the row has more cells than the headers, cut the extra cells
          if (row.length > headersLenght) {
            return row.slice(0, headersLenght)
          } else if (row.length < headersLenght) {
            // When the row has less cells than the headers, filled with an empty string
            const filledRow = []
            for (let i = 0; i < headersLenght; i++) {
              if (row[i] !== undefined) filledRow[i] = row[i]
              else filledRow[i] = STRING.EMPTY_STRING
            }
            return filledRow
          }
          return row
        })
      }
      this.catalogItem.data.headers = headers
      this.catalogItem.data.content = content
      this.sincronizeDataForTable()
    }
  }

  /**
   * Sincronizes the data local with the data form
   * and generates the values with the format required for the data table
   */
  sincronizeDataForTable () {
    this.data = this.catalogItem.data
    this.prepareDataForTable()
  }

  /**
   * Generates the lists to fill the data table: headers and rows.
   *
   * The headers will be a list like:
   *  [{ key: <key1>, label: <label>, sortable: true }, ...]
   *
   * key - to index in table
   * label - to display the real column name
   * sortable - to can sort the columns
   *
   * The rows will be a list like:
   *  [{ key1: <value1>, key2 <value2>, ... }, ...]
   * where each row is going to has as many key-value as headers
   */
  prepareDataForTable () {
    let headers = this.data.headers
    this.dataTableHeaders = []
    headers.forEach(header => {
      this.dataTableHeaders.push({ key: header, label: header, sortable: true })
    })
    this.dataTableContent = []
    this.data.content.forEach(dataRow => {
      const row: any = {}
      for (let i = 0; i < headers.length; i++) {
        const cell = dataRow[i]
        row[headers[i]] = cell

        // Paint the empty cells
        if (cell === STRING.EMPTY_STRING) {
          if ('_cellVariants' in row) {
            row['_cellVariants'][headers[i]] = 'danger'
          } else {
            row['_cellVariants'] = { [headers[i]]: 'danger' }
          }
        }
      }
      this.dataTableContent.push(row)
    })
    this.totalRows = this.dataTableContent.length
  }

  /**
   * Manages the values for display the data in the table
   *
   * @param filteredItems to show in the table
   */
  onFilteredDataTable (filteredItems: any[]) {
    this.totalRows = filteredItems.length
    this.currentPage = 1
  }
}
